/**
 * Comprehensive error logging for authentication failures
 * Реализация рекомендаций Supabase Support
 */

interface AuthLogEntry {
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'debug';
  event: string;
  email?: string;
  userId?: string;
  error?: string;
  metadata?: Record<string, unknown>;
  circuitBreakerState?: unknown;
  networkDiagnostics?: unknown;
}

class AuthLogger {
  private logs: AuthLogEntry[] = [];
  private maxLogs = 100;

  log(entry: Omit<AuthLogEntry, 'timestamp'>): void {
    const logEntry: AuthLogEntry = {
      ...entry,
      timestamp: new Date().toISOString()
    };

    this.logs.unshift(logEntry);
    
    // Keep only recent logs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(0, this.maxLogs);
    }

    // Console output with appropriate level
    const message = `[AuthLogger][${entry.level.toUpperCase()}] ${entry.event}`;
    const data = { ...entry, timestamp: logEntry.timestamp };

    switch (entry.level) {
      case 'error':
        console.error(message, data);
        break;
      case 'warn':
        console.warn(message, data);
        break;
      case 'debug':
        console.debug(message, data);
        break;
      default:
        console.log(message, data);
    }

    // Store in localStorage for debugging
    try {
      localStorage.setItem('auth_logs', JSON.stringify(this.logs.slice(0, 20)));
    } catch (e) {
      // Ignore localStorage errors
    }
  }

  logAuthAttempt(email: string, method: 'signin' | 'signup'): void {
    this.log({
      level: 'info',
      event: `AUTH_ATTEMPT_${method.toUpperCase()}`,
      email,
      metadata: {
        userAgent: navigator.userAgent,
        timestamp: Date.now()
      }
    });
  }

  logAuthSuccess(email: string, userId: string, method: 'signin' | 'signup'): void {
    this.log({
      level: 'info',
      event: `AUTH_SUCCESS_${method.toUpperCase()}`,
      email,
      userId,
      metadata: {
        duration: Date.now()
      }
    });
  }

  logAuthFailure(
    email: string,
    error: string,
    method: 'signin' | 'signup',
    metadata?: Record<string, unknown>
  ): void {
    this.log({
      level: 'error',
      event: `AUTH_FAILURE_${method.toUpperCase()}`,
      email,
      error,
      metadata: {
        ...metadata,
        errorType: this.categorizeError(error)
      }
    });
  }

  logTimeout(email: string, method: 'signin' | 'signup', duration: number): void {
    this.log({
      level: 'error',
      event: `AUTH_TIMEOUT_${method.toUpperCase()}`,
      email,
      error: 'Authentication service timeout',
      metadata: {
        duration,
        timeoutThreshold: 5000
      }
    });
  }

  logCircuitBreakerState(state: unknown): void {
    this.log({
      level: 'warn',
      event: 'CIRCUIT_BREAKER_STATE_CHANGE',
      circuitBreakerState: state,
      metadata: {
        timestamp: Date.now()
      }
    });
  }

  logNetworkDiagnostics(diagnostics: unknown): void {
    this.log({
      level: 'debug',
      event: 'NETWORK_DIAGNOSTICS',
      networkDiagnostics: diagnostics
    });
  }

  private categorizeError(error: string): string {
    if (error.includes('timeout') || error.includes('Timeout')) {
      return 'TIMEOUT';
    }
    if (error.includes('Invalid API key')) {
      return 'INVALID_API_KEY';
    }
    if (error.includes('Rate limit')) {
      return 'RATE_LIMIT';
    }
    if (error.includes('Network')) {
      return 'NETWORK_ERROR';
    }
    if (error.includes('Circuit Breaker')) {
      return 'CIRCUIT_BREAKER';
    }
    return 'UNKNOWN';
  }

  getLogs(limit = 20): AuthLogEntry[] {
    return this.logs.slice(0, limit);
  }

  getErrorSummary(): Record<string, number> {
    const summary: Record<string, number> = {};
    
    this.logs
      .filter(log => log.level === 'error')
      .forEach(log => {
        const errorType = log.metadata?.errorType || 'UNKNOWN';
        summary[errorType] = (summary[errorType] || 0) + 1;
      });

    return summary;
  }

  exportLogs(): string {
    return JSON.stringify({
      exportTime: new Date().toISOString(),
      projectRef: 'aoucmkkqfbqccmcerwtr',
      logs: this.logs,
      summary: this.getErrorSummary()
    }, null, 2);
  }

  clear(): void {
    this.logs = [];
    try {
      localStorage.removeItem('auth_logs');
    } catch (e) {
      // Ignore localStorage errors
    }
  }
}

export const authLogger = new AuthLogger();
